import React, { useEffect, useRef, useState } from 'react';
import config from '../config';
import { VoiceAudio } from './chat/liveVoiceAudio';

// Mode Suara: Gemini voice + GPT-4o Transcribe captions via backend relay.
// Self-contained: owns the WebSocket + audio lifecycles; the parent only
// supplies a conversationId and refetches messages on close (transcripts and
// any comic are persisted server-side while we talk).

interface VoiceModeProps {
  conversationId: number;
  theme: 'light' | 'dark';
  onClose: () => void;
  // Dipanggil saat komik selesai dibangun: parent refetch thread di belakang
  // overlay supaya kartu komiknya SUDAH ada begitu overlay ditutup (tanpa ini
  // refetch saat close bisa balapan dengan persist pesan komik).
  onComicDone?: (comic: { url: string; title: string; messageId?: number }) => void;
}

type Phase = 'connecting' | 'live' | 'error';

const VoiceMode: React.FC<VoiceModeProps> = ({ conversationId, theme, onClose, onComicDone }) => {
  const [phase, setPhase] = useState<Phase>('connecting');
  const [errorMsg, setErrorMsg] = useState('');
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [userLine, setUserLine] = useState('');
  const [aiLine, setAiLine] = useState('');
  const [comicState, setComicState] = useState<'none' | 'drawing' | 'done' | 'failed'>('none');
  const [comicUrl, setComicUrl] = useState('');
  const [comicTitle, setComicTitle] = useState('');

  const wsRef = useRef<WebSocket | null>(null);
  const audioRef = useRef<VoiceAudio | null>(null);
  const closedRef = useRef(false);

  // Keep the display awake for the whole call. The lock is released by the
  // browser whenever the tab is hidden, so request it again on visibility.
  // Unsupported browsers simply keep their normal screen-timeout behaviour.
  useEffect(() => {
    let mounted = true;
    let sentinel: any = null;

    const requestWakeLock = async () => {
      if (!mounted || document.visibilityState !== 'visible' || !(navigator as any).wakeLock) return;
      try {
        sentinel = await (navigator as any).wakeLock.request('screen');
        sentinel.addEventListener?.('release', () => { sentinel = null; });
      } catch {
        // Low-power mode and some embedded browsers can refuse a lock. Voice
        // remains usable; there is no actionable error to show the user.
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !sentinel) void requestWakeLock();
    };

    void requestWakeLock();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisibility);
      try { sentinel?.release(); } catch { /* noop */ }
      sentinel = null;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const wsBase = config.apiUrl.replace(/^http/, 'ws');
    const ws = new WebSocket(
      `${wsBase}/ai/live?token=${encodeURIComponent(token || '')}&conversation_id=${conversationId}`
    );
    wsRef.current = ws;
    const audio = new VoiceAudio();
    audioRef.current = audio;

    ws.onmessage = async (ev) => {
      let msg: any;
      try { msg = JSON.parse(ev.data); } catch { return; }
      switch (msg.type) {
        case 'ready':
          try {
            await audio.start(
              {
                onChunk: (b64, transcriptionB64) => {
                  if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({
                      type: 'audio',
                      data: b64,
                      transcription_data: transcriptionB64,
                    }));
                  }
                },
                onLevel: (l) => setMicLevel(l),
              },
              (active) => setAiSpeaking(active)
            );
            setPhase('live');
          } catch {
            setErrorMsg('Tidak bisa mengakses mikrofon. Izinkan akses mikrofon lalu coba lagi ya.');
            setPhase('error');
          }
          break;
        case 'audio':
          audio.playChunk(msg.data);
          break;
        case 'user_text':
          // OpenAI streaming hypotheses can be corrected by later deltas and
          // by the final transcript. `replace` prevents duplicated words and
          // ensures the stored sentence matches what the user actually said.
          setUserLine((prev) => (
            msg.replace ? String(msg.text || '').slice(-240) : (prev + msg.text).slice(-240)
          ));
          // A new user utterance means the previous AI line is done.
          setAiLine((prev) => (prev.length > 0 ? '' : prev));
          break;
        case 'ai_text':
          setAiLine((prev) => (prev + msg.text).slice(-360));
          break;
        case 'turn_complete':
          setUserLine('');
          break;
        case 'interrupted':
          audio.stopPlayback();
          break;
        case 'comic':
          if (msg.status === 'drawing') {
            setComicState('drawing');
            setComicTitle(msg.title || 'Komik');
          } else if (msg.status === 'done') {
            setComicState('done');
            setComicUrl(msg.url || '');
            setComicTitle(msg.title || 'Komik');
            if (onComicDone) {
              // Backend only emits "done" after the chat message commit, and
              // gives us its id. Parent can render it immediately, independent
              // of a racing conversation refetch.
              onComicDone({
                url: msg.url || '',
                title: msg.title || 'Komik',
                messageId: typeof msg.message_id === 'number' ? msg.message_id : undefined,
              });
            }
          } else if (msg.status === 'failed') {
            setComicState('failed');
          }
          break;
        case 'error':
          setErrorMsg(msg.message || 'Sesi suara terputus.');
          setPhase('error');
          break;
        default:
          break;
      }
    };
    ws.onerror = () => {
      if (!closedRef.current) {
        setErrorMsg('Sesi suara tidak bisa tersambung. Coba lagi ya.');
        setPhase('error');
      }
    };
    ws.onclose = (ev) => {
      if (closedRef.current) return;
      if (ev.code === 4402) {
        setErrorMsg('Kredit kamu belum cukup untuk Mode Suara.');
        setPhase('error');
      } else if (ev.code === 4401) {
        setErrorMsg('Sesi login kamu berakhir. Masuk lagi ya.');
        setPhase('error');
      }
    };

    return () => {
      closedRef.current = true;
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'end' }));
      } catch { /* noop */ }
      try { ws.close(); } catch { /* noop */ }
      audio.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const dark = theme === 'dark';
  const bg = dark ? 'rgba(10,11,13,0.97)' : 'rgba(247,248,250,0.97)';
  const ink = dark ? '#E8EAED' : '#1A1D21';
  const ink3 = dark ? '#8B9099' : '#6B7280';
  const line = dark ? 'rgba(255,255,255,0.10)' : 'rgba(20,22,25,0.12)';
  const JADE = '#5BD6A9';

  const orbScale = phase === 'live' && !aiSpeaking ? 1 + Math.min(0.25, micLevel * 0.6) : 1;
  const status =
    phase === 'connecting' ? 'Menyambungkan…'
    : phase === 'error' ? ''
    : comicState === 'drawing' ? `Menggambar komik "${comicTitle}"…`
    : aiSpeaking ? 'Power AI bicara…'
    : 'Aku dengerin, ngomong aja…';

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 4000, background: bg,
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '24px',
    }}>
      <style>{`
        @keyframes paVoicePulse { 0%,100% { transform: scale(1); opacity: .55; } 50% { transform: scale(1.18); opacity: .2; } }
        @keyframes paVoiceSpeak { 0%,100% { transform: scale(1); } 50% { transform: scale(1.07); } }
        @keyframes paVoiceSpin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 18px',
      }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: '11px',
          letterSpacing: '0.14em', color: ink3, textTransform: 'uppercase',
        }}>
          Power AI · Mode Suara
        </span>
        <button
          onClick={onClose}
          aria-label="Selesai"
          style={{
            appearance: 'none', border: `1px solid ${line}`, background: 'transparent',
            color: ink, borderRadius: '999px', padding: '8px 18px', fontSize: '13.5px',
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          Selesai
        </button>
      </div>

      {/* Orb */}
      <div style={{ position: 'relative', width: 190, height: 190, display: 'grid', placeItems: 'center' }}>
        {/* Halo ring: pulses while connecting, breathes while AI speaks */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: `radial-gradient(circle, ${JADE}33 0%, transparent 70%)`,
          animation: phase === 'connecting'
            ? 'paVoicePulse 1.6s ease-in-out infinite'
            : aiSpeaking ? 'paVoicePulse 1.1s ease-in-out infinite' : 'none',
        }} />
        <div style={{
          width: 132, height: 132, borderRadius: '50%',
          background: `radial-gradient(circle at 34% 30%, ${JADE} 0%, #2E9C74 55%, #1D6B4F 100%)`,
          boxShadow: `0 0 60px ${JADE}44`,
          transform: `scale(${orbScale})`,
          transition: 'transform 0.12s ease-out',
          animation: aiSpeaking ? 'paVoiceSpeak 0.9s ease-in-out infinite' : 'none',
        }} />
        {comicState === 'drawing' && (
          <svg width="190" height="190" viewBox="0 0 190 190" style={{
            position: 'absolute', inset: 0, animation: 'paVoiceSpin 2.4s linear infinite',
          }}>
            <circle cx="95" cy="95" r="88" fill="none" stroke={JADE} strokeWidth="2.5"
              strokeLinecap="round" strokeDasharray="120 440" opacity="0.9" />
          </svg>
        )}
      </div>

      {/* Status */}
      <div style={{ marginTop: 26, minHeight: 24, fontSize: '15px', fontWeight: 600, color: ink }}>
        {status}
      </div>

      {/* Error state */}
      {phase === 'error' && (
        <div style={{ marginTop: 10, maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: ink, lineHeight: 1.55 }}>{errorMsg}</div>
          <button
            onClick={onClose}
            style={{
              marginTop: 16, appearance: 'none', border: 'none', background: JADE,
              color: '#08110D', borderRadius: '999px', padding: '10px 26px',
              fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Tutup
          </button>
        </div>
      )}

      {/* Comic chip */}
      {comicState === 'done' && comicUrl && (
        <a
          href={comicUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 10,
            border: `1px solid ${JADE}66`, background: `${JADE}1A`, color: ink,
            borderRadius: '14px', padding: '11px 18px', textDecoration: 'none',
            fontSize: '14px', fontWeight: 600,
          }}
        >
          <span role="img" aria-label="komik">🎉</span>
          Komik "{comicTitle}" sudah jadi! Lihat yuk
          <span style={{ color: JADE }}>↗</span>
        </a>
      )}
      {comicState === 'failed' && (
        <div style={{ marginTop: 14, fontSize: '13px', color: ink3 }}>
          Komiknya belum berhasil digambar, coba minta lagi ya.
        </div>
      )}

      {/* Live captions */}
      {phase === 'live' && (
        <div style={{
          position: 'absolute', bottom: 34, left: 0, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 8, padding: '0 24px', pointerEvents: 'none',
        }}>
          {userLine && (
            <div style={{
              maxWidth: 560, fontSize: '13.5px', color: ink3, fontStyle: 'italic',
              textAlign: 'center', lineHeight: 1.5,
            }}>
              "{userLine}"
            </div>
          )}
          {aiLine && (
            <div style={{
              maxWidth: 560, fontSize: '14.5px', color: ink, textAlign: 'center',
              lineHeight: 1.55,
            }}>
              {aiLine}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceMode;
